import React, { useState, useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Anchor, Ship, Gift, ArrowLeft, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI } from '@/lib/api';

const AuthScreen = () => {
  const { login, register } = useApp();
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);

  // Login form - remember the last-used email (never the password)
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  useEffect(() => {
    const savedEmail = localStorage.getItem('boat360_last_email');
    if (savedEmail) setLoginEmail(savedEmail);
  }, []);

  // Register form
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regDisplayName, setRegDisplayName] = useState('');
  const [regUsername, setRegUsername] = useState('');
  const [regReferralCode, setRegReferralCode] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(loginEmail, loginPassword);
      localStorage.setItem('boat360_last_email', loginEmail);
      toast.success('Welcome back, Captain!');
    } catch (error) {
      console.error('Login error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Login failed';
      toast.error(`Login failed: ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await register(regEmail, regPassword, regDisplayName, regUsername, regReferralCode);
      toast.success('Welcome aboard, Captain!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await authAPI.forgotPassword({ email: forgotEmail });
      setResetSent(true);
      toast.success('Reset link sent to your email!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset link');
    } finally {
      setIsLoading(false);
    }
  };

  // Forgot Password Screen
  if (showForgotPassword) {
    return (
      <div className="auth-screen min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-cyan-900 p-4">
        <div className="w-full max-w-md mx-auto py-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur rounded-full mb-3">
              <Mail className="w-8 h-8 text-cyan-400" />
            </div>
            <h1 className="text-2xl font-bold text-white">Reset Password</h1>
          </div>

          <Card className="bg-white/10 backdrop-blur border-white/20">
            {resetSent ? (
              <CardContent className="pt-6 space-y-4">
                <div className="text-center text-white">
                  <p className="mb-4">Check your email for a password reset link.</p>
                  <p className="text-cyan-200 text-sm mb-6">Didn't receive it? Check your spam folder.</p>
                </div>
                <Button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetSent(false);
                    setForgotEmail('');
                  }}
                  className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Sign In
                </Button>
              </CardContent>
            ) : (
              <form onSubmit={handleForgotPassword}>
                <CardHeader>
                  <CardTitle className="text-white">Forgot Password?</CardTitle>
                  <CardDescription className="text-cyan-200">
                    Enter your email and we'll send you a reset link
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email" className="text-white">Email</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="captain@boat.com"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                      data-testid="forgot-email"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
                    disabled={isLoading}
                    data-testid="forgot-submit"
                  >
                    {isLoading ? 'Sending...' : 'Send Reset Link'}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(false)}
                    className="w-full text-sm text-cyan-300 hover:text-cyan-200 flex items-center justify-center gap-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Sign In
                  </button>
                </CardContent>
              </form>
            )}
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-screen min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-cyan-900 p-4">
      <div className="w-full max-w-md mx-auto py-8">
        {/* Logo */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur rounded-full mb-3">
            <Ship className="w-8 h-8 text-cyan-400" />
          </div>
          <h1 className="text-2xl font-bold text-white">Boat 360</h1>
          <p className="text-cyan-200 text-sm">Stay connected on the water</p>
        </div>

        <Card className="bg-white/10 backdrop-blur border-white/20">
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-white/10">
              <TabsTrigger value="login" className="data-[state=active]:bg-white/20 text-white">
                Sign In
              </TabsTrigger>
              <TabsTrigger value="register" className="data-[state=active]:bg-white/20 text-white">
                Sign Up
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin}>
                <CardHeader>
                  <CardTitle className="text-white">Welcome Back</CardTitle>
                  <CardDescription className="text-cyan-200">
                    Sign in to your account
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-white">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      name="email"
                      autoComplete="username"
                      placeholder="captain@boat.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                      data-testid="login-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-white">Password</Label>
                    <Input
                      id="login-password"
                      type="password"
                      name="password"
                      autoComplete="current-password"
                      placeholder="••••••••"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                      data-testid="login-password"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
                    disabled={isLoading}
                    data-testid="login-submit"
                  >
                    {isLoading ? 'Signing in...' : 'Sign In'}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="w-full text-sm text-cyan-300 hover:text-cyan-200 underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot your password?
                  </button>
                </CardContent>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister}>
                <CardHeader>
                  <CardTitle className="text-white">Create Account</CardTitle>
                  <CardDescription className="text-cyan-200">
                    Join the boating community
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reg-name" className="text-white">Display Name</Label>
                    <Input
                      id="reg-name"
                      type="text"
                      placeholder="Captain Jack"
                      value={regDisplayName}
                      onChange={(e) => setRegDisplayName(e.target.value)}
                      required
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                      data-testid="register-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-username" className="text-white">Username</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50">@</span>
                      <Input
                        id="reg-username"
                        type="text"
                        placeholder="captainjack"
                        value={regUsername}
                        onChange={(e) => setRegUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                        required
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/50 pl-8"
                        data-testid="register-username"
                      />
                    </div>
                    <p className="text-xs text-cyan-200/70">
                      Lowercase letters, numbers, and underscores only
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-email" className="text-white">Email</Label>
                    <Input
                      id="reg-email"
                      type="email"
                      name="email"
                      autoComplete="email"
                      placeholder="captain@boat.com"
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                      required
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                      data-testid="register-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-password" className="text-white">Password</Label>
                    <Input
                      id="reg-password"
                      type="password"
                      name="new-password"
                      autoComplete="new-password"
                      placeholder="••••••••"
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                      required
                      minLength={6}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                      data-testid="register-password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-referral" className="text-white flex items-center gap-2">
                      <Gift className="w-4 h-4" />
                      Referral Code (optional)
                    </Label>
                    <Input
                      id="reg-referral"
                      type="text"
                      placeholder="FRIEND123"
                      value={regReferralCode}
                      onChange={(e) => setRegReferralCode(e.target.value.toUpperCase())}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                      data-testid="register-referral"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
                    disabled={isLoading}
                    data-testid="register-submit"
                  >
                    {isLoading ? 'Creating account...' : 'Create Account'}
                  </Button>
                </CardContent>
              </form>
            </TabsContent>
          </Tabs>
        </Card>

        <p className="text-center text-cyan-200/60 text-sm mt-6">
          <Anchor className="inline w-4 h-4 mr-1" />
          Stay safe on the water
        </p>
      </div>
    </div>
  );
};

export default AuthScreen;
